import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { DeleteInsuranceProductModal } from 'src/components/insurance-product/delete/DeleteInsuranceProductModal'
import { InsuranceProductFormModal } from 'src/components/insurance-product/insurance-product-form/InsuranceProductFormModal'
import type {
  InsuranceProductFormMode,
  InsuranceProductFormValues,
} from 'src/components/insurance-product/insurance-product-form/InsuranceProductForm.interface'
import { InsuranceProductsTable } from 'src/components/insurance-product/insurance-products-table/InsuranceProductsTable'
import { InsuranceProductsTableToolbar } from 'src/components/insurance-product/insurance-products-table/InsuranceProductsTableToolbar'
import { Button } from 'src/components/shared/Button'
import {
  AdminCreateInsuranceProduct,
  AdminDeleteInsuranceProduct,
  AdminFetchInsuranceProducts,
  AdminUpdateInsuranceProduct,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'
import {
  getInsuranceProductDeletingId,
  getInsuranceProductSaving,
} from 'src/selectors/insuranceProduct'
import { blankToUndefined } from 'src/utils/blankToUndefined'
import { GSIcon } from 'src/components/shared/GSIcon'

interface FormModalState {
  mode: InsuranceProductFormMode
  insuranceProduct: InsuranceProduct | null
}

const InsuranceProductsPageComponent = () => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const [searchQuery, setSearchQuery] = useState<string>('')
  // Bumped by the Refresh button and by a successful create, update or delete — included in the
  // fetch effect's dependency array below so any of them re-triggers the request.
  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [formModalState, setFormModalState] = useState<FormModalState | null>(null)
  const [productPendingDelete, setProductPendingDelete] = useState<InsuranceProduct | null>(null)

  const { isAuthenticated } = useAuthSession()
  const isSaving = useSelector(getInsuranceProductSaving)
  const deletingId = useSelector(getInsuranceProductDeletingId)
  // Add, Edit and Delete all call `insurance_products_manage` endpoints (ENG-1207 part 2).
  const canManage = useHasPermission(InternalPermission.InsuranceProductsManage)

  /** The pending row's own in-flight state — drives both the dismissal guard and the modal. */
  const isDeletingPendingRow =
    productPendingDelete !== null && deletingId === productPendingDelete.id

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // The single fetch effect: the endpoint takes no search or pagination params and no
  // customer scope, so this only reruns on an auth change or a manual refresh.
  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchInsuranceProducts.request())
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken])

  const handleOpenCreate = useCallback(() => {
    setFormModalState({ mode: 'create', insuranceProduct: null })
  }, [])

  const handleOpenEdit = useCallback((insuranceProduct: InsuranceProduct) => {
    setFormModalState({ mode: 'edit', insuranceProduct })
  }, [])

  // Guarded on `isSaving`, which covers every dismissal path at once — Cancel, the modal's
  // close button, and a backdrop click. Without it, an admin could dismiss a saving form, open
  // another row, and have the first request's success handler close the SECOND dialog and
  // discard its unsaved input.
  const handleCloseFormModal = useCallback(() => {
    if (isSaving) {
      return
    }

    setFormModalState(null)
  }, [isSaving])

  const handleOpenDelete = useCallback((insuranceProduct: InsuranceProduct) => {
    setProductPendingDelete(insuranceProduct)
  }, [])

  // Guarded the same way, and this is also what keeps `deletingId` correct as a single value
  // rather than a set: only one row can be pending deletion at a time, and while its request is
  // in flight the modal cannot be dismissed — so a second delete cannot be started, and one
  // completing request can never clear another's in-flight state.
  const handleCloseDeleteModal = useCallback(() => {
    if (isDeletingPendingRow) {
      return
    }

    setProductPendingDelete(null)
  }, [isDeletingPendingRow])

  const handleSubmit = useCallback(
    async (values: InsuranceProductFormValues) => {
      if (!formModalState) {
        return
      }

      const name = values.name.trim()
      const description = blankToUndefined(values.description)
      const explanation = blankToUndefined(values.explanation)

      if (formModalState.mode === 'edit') {
        if (!formModalState.insuranceProduct) {
          return
        }

        const updated = await asyncDispatchWithErrorToast<InsuranceProduct>(
          AdminUpdateInsuranceProduct.request({
            insuranceProductId: formModalState.insuranceProduct.id,
            name,
            description,
            explanation,
          }),
        )

        if (!updated) {
          return
        }

        snackbar?.enqueueSnackbar('Insurance product updated', { variant: 'success' })
        setFormModalState(null)
        handleRefresh()

        return
      }

      const created = await asyncDispatchWithErrorToast<InsuranceProduct>(
        AdminCreateInsuranceProduct.request({ name, description, explanation }),
      )

      if (!created) {
        return
      }

      snackbar?.enqueueSnackbar('Insurance product added', { variant: 'success' })
      setFormModalState(null)
      handleRefresh()
    },
    [formModalState, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!productPendingDelete) {
      return
    }

    // A rejected delete (403 'Insurance product is in use') resolves to null here, and the
    // error toast has already been raised — the modal stays open so the admin can read it
    // against the row they picked.
    const deleted = await asyncDispatchWithErrorToast<string>(
      AdminDeleteInsuranceProduct.request({ insuranceProductId: productPendingDelete.id }),
    )

    if (!deleted) {
      return
    }

    snackbar?.enqueueSnackbar('Insurance product deleted', { variant: 'success' })
    setProductPendingDelete(null)
    handleRefresh()
  }, [productPendingDelete, asyncDispatchWithErrorToast, snackbar, handleRefresh])

  return (
    <Fragment>
      <div data-testid="admin-insurance-products-page" css={tw`min-w-0 flex-1 overflow-y-auto p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div>
            <Typography variant="h2">Insurance Products</Typography>
          </div>
          {canManage ? (
            <Button
              size="small"
              color="dark"
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenCreate}
              data-testid="insurance-products-list-add-button"
            >
              Add product
            </Button>
          ) : null}
        </div>
        <InsuranceProductsTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
        <InsuranceProductsTable
          searchQuery={searchQuery}
          canManage={canManage}
          onEditInsuranceProduct={handleOpenEdit}
          onDeleteInsuranceProduct={handleOpenDelete}
        />
      </div>
      <InsuranceProductFormModal
        isOpen={Boolean(formModalState)}
        mode={formModalState?.mode ?? 'create'}
        insuranceProduct={formModalState?.insuranceProduct ?? null}
        isSaving={isSaving}
        onSubmit={handleSubmit}
        onClose={handleCloseFormModal}
      />
      <DeleteInsuranceProductModal
        isOpen={Boolean(productPendingDelete)}
        insuranceProduct={productPendingDelete}
        isDeleting={isDeletingPendingRow}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteModal}
      />
    </Fragment>
  )
}

export const InsuranceProductsPage = memo(InsuranceProductsPageComponent)
